import { AccessToken, DefaultTokenStore } from "@meiden-connect/auth-frontend-sdk";
import { getWebConfig } from "../../env";
import { loadSessionSnapshot } from "../../auth/storage";

/** トークン読み取り全体の打ち切り時間（ms）。 */
const READ_DEADLINE_MS = 5000;
/** この時間より古いアクセストークンは読み取り時に更新させる（連続更新の抑止も兼ねる）。 */
const REFRESH_IF_OLDER_THAN_MS = 60_000;
/** 残り有効期間がこれ以下のトークンは渡さない（子が使う前に失効するため）。 */
const MINIMUM_REMAINING_MS = 30_000;

/** 同時に 2 本の読み取りを走らせないための番兵（SDK の close が実行中の更新を待つため）。 */
let pendingStore: Promise<void> | undefined;

/**
 * Connect Feedback 単独 audience の短命アクセストークンだけを読む。
 *
 * 認可フロー（リダイレクト）は起動せず、公開 SDK の `DefaultTokenStore` から
 * 既存のリフレッシュトークン経由で取得するだけにする。読み取りに失敗しても
 * 本アプリのセッション（`SessionSnapshot`）へは一切触れない（付加機能のため）。
 * トークンの値と例外の内容はログへ出さない。
 */
export async function readFeedbackAccessToken(
  origin: string,
  userId: string
): Promise<string | undefined> {
  const config = getWebConfig();
  const authServiceOrigin = config.authServiceOrigin;
  const serviceOrigin = config.serviceOrigin;
  const session = loadSessionSnapshot();
  if (
    config.authMode === "mock" ||
    authServiceOrigin === undefined ||
    serviceOrigin === undefined ||
    session?.source !== "meiden-connect" ||
    session.userId !== userId ||
    pendingStore !== undefined
  ) {
    return undefined;
  }

  const store = new DefaultTokenStore({
    authServiceOrigin,
    serviceOrigin,
    // SDK の内部ログにトークン由来の情報が出ないよう、無音のロガーを渡す。
    logger: { debug() {}, info() {}, warn() {}, error() {} },
  });
  let expired = false;
  let closing: Promise<void> | undefined;
  const close = (): Promise<void> => {
    closing ??= Promise.resolve()
      .then(() => store.close())
      .catch(() => undefined);
    return closing;
  };

  const read = async (): Promise<string | undefined> => {
    try {
      const tokens = await store.readTokenSet(userId, [origin], {
        refreshIfOlderThan: new Date(Date.now() - REFRESH_IF_OLDER_THAN_MS),
      });
      if (expired || !tokens || loadSessionSnapshot()?.userId !== userId) {
        return undefined;
      }
      const token = new AccessToken(tokens.accessToken, origin);
      if (
        token.userId !== userId ||
        token.issuer !== authServiceOrigin ||
        token.audience.length !== 1 ||
        token.audience[0] !== origin ||
        !Number.isFinite(token.payload.exp) ||
        token.payload.exp * 1000 <= Date.now() + MINIMUM_REMAINING_MS
      ) {
        return undefined;
      }
      return tokens.accessToken;
    } catch {
      // 失敗の詳細は握りつぶす（トークン由来の情報をログへ出さないため）。
      return undefined;
    } finally {
      await close();
    }
  };

  const operation = read();
  // SDK の close は実行中の更新を待つため、回収前に別の更新を重ねない。
  const settled = operation.then(() => undefined);
  const releasePendingStore = (): void => {
    if (pendingStore === settled) pendingStore = undefined;
  };
  pendingStore = settled;
  void settled.then(releasePendingStore);

  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => {
      expired = true;
      void close();
      // 期限超過した読み取りの結果はもう採用しない。SDK 側が決着しないまま
      // 残る場合に番兵を握り続けると、以後の読み取りが恒久的に塞がるため、
      // ここで解放して再試行（フォーカス復帰・オンライン復帰など）を許す。
      releasePendingStore();
      resolve(undefined);
    }, READ_DEADLINE_MS);
  });
  const result = await Promise.race([operation, deadline]);
  clearTimeout(timer);
  return !expired && loadSessionSnapshot()?.userId === userId ? result : undefined;
}
